/**
 * Which language the SELLER reads the theme editor in (feature 0051 lot 4).
 *
 * ## The constraint the analysis missed: the builder is CROSS-ORIGIN
 *
 * §2.4 treated the builder as "another surface of the dashboard". It is not: `deriveBuilderUrl()`
 * sends the seller from `admin.<x>` to `builder.<x>`, a different origin with its own `localStorage`.
 * The dashboard's `aqx.admin.locale` is unreadable from here — so the reading language has to be
 * CARRIED across, and nothing in the existing plumbing carried it.
 *
 * ## Why not `?locale=`
 *
 * Because that parameter is already taken, and by the opposite thing: `?locale=` is the SHOP's
 * language (feature 0050) — which page translation the seller is editing. A shop edited in Arabic can
 * be edited by a seller who reads French, and reusing one parameter for both would make that
 * impossible to express. Hence `?ui=`, which names what it is: the language of the interface.
 *
 * ## Two sources, in order
 *
 *   1. `?ui=<locale>` — the dashboard puts it on the link, so the FIRST PAINT is already right. A
 *      round trip to the server would render the whole inspector in English and then swap it.
 *   2. this origin's own storage — a bookmarked builder URL, or a refresh that dropped the query,
 *      still opens in the language the seller last read.
 *
 * The floor is English: the language the schemas are authored in, so a builder that learns nothing
 * renders exactly what it renders today.
 */

/**
 * The languages the editor is DELIVERED in — the same set as the dashboard's `DELIVERED_LOCALES`,
 * and it has to stay that way. A seller carries their reading language across the origin boundary in
 * `?ui=`; a code the dashboard offers and the editor does not know falls back to English, which is
 * the half-translated state the whole feature refuses.
 */
export const BUILDER_LOCALES = ['en', 'fr', 'ar', 'es', 'it', 'de', 'pt', 'pl'] as const;

export type BuilderLocale = (typeof BUILDER_LOCALES)[number];

/** English, because that is the language the ~2 400 schema labels are written in. */
export const FLOOR_LOCALE: BuilderLocale = 'en';

const STORAGE_KEY = 'aqx.builder.ui-locale';

function isBuilderLocale(value: unknown): value is BuilderLocale {
  return typeof value === 'string' && (BUILDER_LOCALES as readonly string[]).includes(value);
}

function fromQuery(): BuilderLocale | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('ui');
  return isBuilderLocale(raw) ? raw : null;
}

function fromStorage(): BuilderLocale | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isBuilderLocale(raw) ? raw : null;
  } catch {
    // Private browsing, or storage refused. Never a reason to fail to open the editor.
    return null;
  }
}

function remember(locale: BuilderLocale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    /* see fromStorage */
  }
}

/**
 * The locale to render in. Resolved once, at module load: the seller does not change their reading
 * language from inside the editor — they change it in the dashboard, and arrive here with a new `?ui=`.
 */
let resolved: BuilderLocale = (() => {
  const fromUrl = fromQuery();
  if (fromUrl) {
    remember(fromUrl);
    return fromUrl;
  }
  return fromStorage() ?? FLOOR_LOCALE;
})();

export function builderLocale(): BuilderLocale {
  return resolved;
}

/**
 * The CHROME's direction — the seller's reading direction, nothing to do with the shop's.
 * Founder, 02/09: « même le builder doit être en arabe si on est en arabe ». The canvas preview
 * keeps its own `dir` (the SHOP's language, feature 0019) and overrides this on its subtree.
 */
export function builderDir(): 'ltr' | 'rtl' {
  return resolved === 'ar' ? 'rtl' : 'ltr';
}

/** Test seam: read the editor in a chosen language without a URL or a storage. Never app code. */
export function __setBuilderLocaleForTests(locale: BuilderLocale): void {
  resolved = locale;
}

/** Test seam. Never called by app code. */
export function __resolveBuilderLocaleForTests(
  search: string,
  stored: string | null,
): BuilderLocale {
  const raw = new URLSearchParams(search).get('ui');
  if (isBuilderLocale(raw)) return raw;
  return isBuilderLocale(stored) ? stored : FLOOR_LOCALE;
}
